import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import LOGO from "../../../assets/img/MOSIDA-logo.png";
import FifaBg from "../../../assets/img/fifa/fifa-inner.png";
import BackArrow from "../../../compoment/icon/BackArrow";
import type { RootState } from "../../../redux/store";
import { allowOnlyOtpDigits } from "../../../modal/auth/phoneUtils";
import {
  clearFifaPlayerStats,
  DEFAULT_FIFA_PLAYER_STATS,
  isFifaWorldCup2026Station,
  persistFifaPlayerStats,
  readActiveStoredStation,
  readFifaPlayerProfile,
  readFifaPlayerStats,
  resolveFifaCapturedImage,
  resolveFifaStationSlug,
  type FifaPlayerProfile,
  type FifaPlayerStats,
  type FifaStatKey,
} from "../../../utils/stationFlowStorage";
import { readSelectedAiSelfieArtFromStorage } from "../../../services/stylePromptResolver";
import FifaThemeDropdown from "../../../compoment/ui/FifaThemeDropdown";

const FOOTBALL_POSITIONS: Array<{ key: string; value: string; abbr: string }> = [
  { key: "Goalkeeper", value: "Goalkeeper", abbr: "GK" },
  { key: "Right Back", value: "Right Back", abbr: "RB" },
  { key: "Left Back", value: "Left Back", abbr: "LB" },
  { key: "Center Back", value: "Center Back", abbr: "CB" },
  { key: "Right Wing Back", value: "Right Wing Back", abbr: "RWB" },
  { key: "Left Wing Back", value: "Left Wing Back", abbr: "LWB" },
  { key: "Central Defensive Midfielder", value: "Central Defensive Midfielder", abbr: "CDM" },
  { key: "Central Midfielder", value: "Central Midfielder", abbr: "CM" },
  { key: "Central Attacking Midfielder", value: "Central Attacking Midfielder", abbr: "CAM" },
  { key: "Right Midfielder", value: "Right Midfielder", abbr: "RM" },
  { key: "Left Midfielder", value: "Left Midfielder", abbr: "LM" },
  { key: "Right Winger", value: "Right Winger", abbr: "RW" },
  { key: "Left Winger", value: "Left Winger", abbr: "LW" },
  { key: "Center Forward", value: "Center Forward", abbr: "CF" },
  { key: "Striker", value: "Striker", abbr: "ST" },
];

const POSITION_DROPDOWN_OPTIONS = FOOTBALL_POSITIONS.map((position) => ({
  value: position.abbr,
  label: position.value,
  shortLabel: position.abbr,
}));

const FIFA_STAT_FIELDS: Array<{
  key: FifaStatKey;
  abbr: string;
  label: string;
}> = [
    { key: "pac", abbr: "PAC", label: "PACE" },
    { key: "sho", abbr: "SHO", label: "SHOOTING" },
    { key: "pas", abbr: "PAS", label: "PASSING" },
    { key: "dri", abbr: "DRI", label: "DRIBBLING" },
    { key: "def", abbr: "DEF", label: "DEFENDING" },
    { key: "phy", abbr: "PHY", label: "PHYSICAL" },
  ];

const MIN_STAT = 1;
const MAX_STAT = 99;

const EnterStats = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const uploadedImageUrl = useSelector(
    (state: RootState) => state.uploadFlow.uploadedImageUrl
  );

  const stationData = useMemo(() => readActiveStoredStation(), [location.state]);
  const selectedAiSelfieArt = useMemo(
    () => readSelectedAiSelfieArtFromStorage(),
    [location.state]
  );
  const stationSlug = useMemo(
    () => resolveFifaStationSlug(stationData, selectedAiSelfieArt),
    [stationData, selectedAiSelfieArt]
  );
  const isFifaFlow = isFifaWorldCup2026Station(stationData, selectedAiSelfieArt);
  const locationCapturedImage = (location.state as { capturedImage?: string } | null)
    ?.capturedImage;
  const capturedImage = resolveFifaCapturedImage({
    locationImage: locationCapturedImage,
    reduxImage: uploadedImageUrl,
  });
  const fifaPlayerProfile =
    (location.state as { fifaPlayerProfile?: FifaPlayerProfile } | null)
      ?.fifaPlayerProfile || readFifaPlayerProfile();

  const [stats, setStats] = useState<FifaPlayerStats>(() => {
    const saved = readFifaPlayerStats();
    const merged = {
      ...DEFAULT_FIFA_PLAYER_STATS,
      ...saved,
    };

    const matchedPosition = FOOTBALL_POSITIONS.find(
      (position) =>
        position.value === merged.position || position.abbr === merged.position
    );

    if (matchedPosition) {
      merged.position = matchedPosition.abbr;
    }

    return merged;
  });

  useEffect(() => {
    if (!isFifaFlow) {
      navigate(`/${stationSlug}/choose-option`, { replace: true });
      return;
    }

    if (!capturedImage || !fifaPlayerProfile) {
      navigate(`/${stationSlug}/upload-fifa-image`, { replace: true });
    }
  }, [capturedImage, fifaPlayerProfile, isFifaFlow, navigate, stationSlug]);

  const routeState = {
    capturedImage,
    stationData,
    fifaPlayerProfile,
    fifaStatsSource: "manual" as const,
  };

  const adjustStat = (key: FifaStatKey, delta: number) => {
    setStats((prev) => ({
      ...prev,
      [key]: Math.min(MAX_STAT, Math.max(MIN_STAT, prev[key] + delta)),
    }));
  };

  const handleProceed = () => {
    if (!stats.rating.trim() || !capturedImage) return;

    persistFifaPlayerStats(stats);
    navigate(`/${stationSlug}/creating-fifa-card`, {
      state: {
        ...routeState,
        fifaPlayerStats: stats,
      },
    });
  };

  const handleSkip = () => {
    clearFifaPlayerStats();
    navigate(`/${stationSlug}/creating-fifa-card`, {
      state: {
        capturedImage,
        stationData,
        fifaPlayerProfile,
        fifaStatsSource: "ai",
        fifaStatsSkipped: true,
      },
    });
  };

  if (!isFifaFlow || !capturedImage || !fifaPlayerProfile) {
    return null;
  }

  const handleBack = () => {
    clearFifaPlayerStats();
    setStats({ ...DEFAULT_FIFA_PLAYER_STATS });
    navigate(`/${stationSlug}/fifa-stats-choice`, {
      state: {
        capturedImage,
        stationData,
        fifaPlayerProfile,
      },
    });
  };

  return (
    <section
      className="enterStatsSection"
      style={{ backgroundImage: `url(${FifaBg})` }}
    >
      <div className="enterStatsInner">
        <div className="mosidaLogoWhite enterStatsLogo mb-5">
          <img src={LOGO} alt="MOSIDA logo" className="logo" />
          <span className="badgeCreative">FIFA World Cup</span>
        </div>

        <button
          type="button"
          className="enterStatsBack"
          onClick={handleBack}
        >
          <span className="me-1">
            <BackArrow />
          </span>
          Back
        </button>

        <div className="enterStatsBody">
          <h1 className="enterStatsHeading mb-5 pb-4">
            <div className="enterStatsHeadingLine">
              ENTER{" "}
              <span className="enterStatsHeadingLine--gold ">YOUR STATS</span>
            </div>
          </h1>

          <div className="enterStatsForm">
            <div className="enterStatsTopRow">
              <label className="enterStatsField">
                <span className="enterStatsLabel">POSITION</span>
                <FifaThemeDropdown
                  value={stats.position}
                  onChange={(value) =>
                    setStats((prev) => ({ ...prev, position: value }))
                  }
                  options={POSITION_DROPDOWN_OPTIONS}
                  placeholder="SELECT POSITION"
                  searchable={false}
                  ariaLabel="Position"
                />
              </label>

              <label className="enterStatsField">
                <span className="enterStatsLabel">RATING</span>
                <input
                  type="text"
                  className="enterStatsInput"
                  placeholder="ENTER RATING"
                  value={stats.rating}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onChange={(event) =>
                    setStats((prev) => ({
                      ...prev,
                      rating: allowOnlyOtpDigits(event.target.value, 2),
                    }))
                  }
                />
              </label>
            </div>

            <div className="enterStatsGrid">
              {FIFA_STAT_FIELDS.map(({ key, abbr, label }) => (
                <div key={key} className="enterStatsCard">
                  <div className="enterStatsCardHeading">
                    <span className="enterStatsCardAbbr">{abbr}</span>
                    <span className="enterStatsCardLabel">{label}</span>
                  </div>
                  <div className="enterStatsCardControl">
                    <span className="enterStatsCardValue">{stats[key]}</span>
                    <div className="enterStatsCardButtons">
                      <button
                        type="button"
                        className="enterStatsAdjustBtn"
                        aria-label={`Decrease ${label}`}
                        onClick={() => adjustStat(key, -1)}
                        disabled={stats[key] <= MIN_STAT}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-minus-icon lucide-minus"><path d="M5 12h14" /></svg>
                      </button>
                      <button
                        type="button"
                        className="enterStatsAdjustBtn"
                        aria-label={`Increase ${label}`}
                        onClick={() => adjustStat(key, 1)}
                        disabled={stats[key] >= MAX_STAT}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-plus-icon lucide-plus"><path d="M5 12h14" /><path d="M12 5v14" /></svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="enterStatsProceedBtn mt-4"
              disabled={!stats.rating.trim()}
              onClick={handleProceed}
            >
              PROCEED
            </button>

            <button
              type="button"
              className="enterStatsSkipBtn mt-4"
              onClick={handleSkip}
            >
              SKIP
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EnterStats;
